import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { auth, db } from "./firebase"; 
import styled from "styled-components";
import { onAuthStateChanged } from "firebase/auth";
import AppHeader from "./AppHeader";

const BusinessView = () => {
  // useParams() vazhi businessName params edukkunnu
  const { businessName } = useParams(); 
  const location = useLocation();
  const navigate = useNavigate();
  const { businessId: stateId, businessName: stateBusinessName } = location.state || {}; 
  
  const [businessData, setBusinessData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followers, setFollowers] = useState([]); 
  const [following, setFollowing] = useState([]); 
  const [currentUser, setCurrentUser] = useState(null);

  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*/g;
  
  const getYouTubeId = (text) => {
    if (!text) return null;
    const match = new RegExp(youtubeRegex).exec(text);
    return match ? match[1] : null;
  };

  const renderTextWithoutURL = (text) => {
    if (!text) return "";
    return text.replace(youtubeRegex, "").replace(/&nbsp;/g, ' ').trim();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    try {
        const date = timestamp.toDate();
        return date.toLocaleDateString('en-GB'); 
    } catch(e) { return ""; }
  };

  const updateFavicon = (url) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = url || "/favicon.ico";
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null);
    });

    const fetchData = async () => {
      setLoading(true);
      let finalBusinessId = stateId;

      // useParams-il ninnu kittunna name params businessName aanennu urappu varuthunnu
      const urlSlug = businessName;

      try {
        // Incognito mode-il stateId undavilla, appol businessName upayogichu ID thirayuunnu
        if (!finalBusinessId && urlSlug) {
          console.log("Searching for business with slug:", urlSlug);
          const bizRef = collection(db, "businessPages");
          const querySnapshot = await getDocs(bizRef);
          
          const foundBiz = querySnapshot.docs.find(doc => {
            const bName = doc.data().businessName || "";
            const cleanDBName = bName.toLowerCase().replace(/[^a-z0-9]/g, "");
            const cleanURLSlug = urlSlug.toLowerCase().replace(/[^a-z0-9]/g, "");
            return cleanDBName === cleanURLSlug;
          });

          if (foundBiz) {
            finalBusinessId = foundBiz.id;
          }
        }

        if (!finalBusinessId) {
          console.error("No Business ID found for slug:", urlSlug);
          setLoading(false);
          return;
        }

        // 1. Fetch Business Details
        const docRef = doc(db, "businessPages", finalBusinessId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setBusinessData(data);
          document.title = `${data.businessName || "Business"} - | Avaami Business`;
          if (data.logoUrl) updateFavicon(data.logoUrl);
        }

        // 2. Fetch Posts
        const postsRef = collection(db, "businessPosts");
        const qPosts = query(postsRef, where("businessId", "==", finalBusinessId));
        const postSnapshot = await getDocs(qPosts);
        
        // Firestore Index error ozhivakkan vendi client-side sorting upayogikkunnu
        const sortedPosts = postSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setPosts(sortedPosts);

        // 3. Fetch Followers & Following
        const followersRef = collection(db, "businessFollowers");
        const qFollowers = query(followersRef, where("businessId", "==", finalBusinessId));
        const followerSnapshot = await getDocs(qFollowers);
        setFollowers(followerSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const qFollowing = query(followersRef, where("followerUid", "==", finalBusinessId));
        const followingSnapshot = await getDocs(qFollowing);
        setFollowing(followingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
      } catch (error) {
        console.error("Error in fetchData:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      unsubscribe();
      document.title = "Avaami Business"; 
      updateFavicon("/favicon.ico");
    };
  }, [stateId, businessName]); // Dependency array-il businessName sariyayi nalki

  const handleViewProfile = (f) => {
    const nameSlug = (f.followerName || "").replace(/[\s-]/g, '');
    navigate(`/profile/${nameSlug}`, { state: { uid: f.followerUid } });
  };

  const handleBusinessClick = (f) => {
    if (f.followerBusinessName) {
      const bizSlug = f.followerBusinessName.toLowerCase().replace(/[\s-]/g, '');
      navigate(`/business-view/${bizSlug}`, { 
        state: { businessId: f.followerUid, businessName: f.followerBusinessName } 
      });
    }
  };

  if (loading) return (
    <>
      <AppHeader />
      <LoadingContainer>Loading Profile...</LoadingContainer>
    </>
  );

  return (
    <>
      <AppHeader />
      <PageWrapper>
        <HeaderCard>
          <CoverImage src={businessData?.coverImageUrl || "/default-cover.jpg"} alt="Cover" />
          <ProfileBranding>
            <LogoWrapper>
              <Logo src={businessData?.logoUrl || "/default-logo.png"} alt="Logo" />
            </LogoWrapper>
            <BusinessInfoMain>
              <h1>{businessData?.businessName || stateBusinessName || "Business Profile"}</h1>
              <p className="tagline">{businessData?.tagline}</p>
              <StatsContainer>
                <StatBadge>{followers.length} Followers</StatBadge>
                <StatBadge style={{background: '#64748b'}}>{following.length} Following</StatBadge>
              </StatsContainer>
            </BusinessInfoMain>
          </ProfileBranding>
        </HeaderCard>

        <ContentGrid>
          <Sidebar>
            <InfoCard>
              <h3>About</h3>
              <p className="description">{businessData?.description || "No description available."}</p>
              <Divider />
              <ContactList>
                <li><strong>Location:</strong> {businessData?.location || "N/A"}</li>
              </ContactList>
            </InfoCard>

            <InfoCard style={{marginTop: '20px'}}>
              <h3>Following</h3>
              {following.length > 0 ? (
                <FollowersGrid style={{gridTemplateColumns: '1fr'}}>
                   {following.map(f => (
                    <FollowerCard key={f.id} style={{flexDirection: 'row', padding: '10px', gap: '10px'}}>
                      <FollowerImageContainer style={{width: '40px', height: '40px', marginBottom: '0'}}>
                        <img src={f.followerImage || "/default-avatar.png"} alt={f.businessName} />
                      </FollowerImageContainer>
                      <div style={{textAlign: 'left'}}>
                        <FollowerName style={{fontSize: '13px'}}>{f.businessName}</FollowerName>
                      </div>
                    </FollowerCard>
                  ))}
                </FollowersGrid>
              ) : (
                <p style={{fontSize: '14px', color: '#666'}}>Not following anyone.</p>
              )}
            </InfoCard>
          </Sidebar>

          <MainFeed>
            <FollowersSection>
              <h3>Followers</h3>
              {followers.length > 0 ? (
                <FollowersGrid>
                  {followers.map(f => (
                    <FollowerCard key={f.id}>
                      <FollowerImageContainer>
                        <img src={f.followerImage || "/default-avatar.png"} alt={f.followerName} />
                      </FollowerImageContainer>
                      <FollowerName>{f.followerName || "User"}</FollowerName>
                      {f.mainRole === "Business Owner" && f.followerBusinessName && (
                        <BusinessLink onClick={() => handleBusinessClick(f)}>
                          {f.followerBusinessName}
                        </BusinessLink>
                      )}
                      <FollowerType>{f.mainRole || "Follower"}</FollowerType>
                      <ViewProfileButton onClick={() => handleViewProfile(f)}>
                        View Profile
                      </ViewProfileButton>
                    </FollowerCard>
                  ))}
                </FollowersGrid>
              ) : (
                <p>No followers yet.</p>
              )}
            </FollowersSection>

            <h3 style={{marginTop: '30px'}}>Feed Updates</h3>
            {posts.length > 0 ? posts.map(post => {
              const ytId = getYouTubeId(post.text);
              const cleanText = renderTextWithoutURL(post.text);
              return (
                <PostCard key={post.id}>
                  <PostDateTop>{formatDate(post.createdAt)}</PostDateTop>
                  {post.text && <PostText dangerouslySetInnerHTML={{ __html: cleanText }} />}
                  {ytId && (
                    <VideoWrapper>
                      <iframe src={`https://www.youtube.com/embed/${ytId}`} title="YT" frameBorder="0" allowFullScreen />
                    </VideoWrapper>
                  )}
                  {post.mediaUrl && !ytId && <PostMedia src={post.mediaUrl} alt="Post" />}
                </PostCard>
              );
            }) : <p>No updates yet.</p>}
          </MainFeed>
        </ContentGrid>
      </PageWrapper>
    </>
  );
};

// Styled Components (No changes here)
const PageWrapper = styled.div`max-width: 1100px; margin: 0 auto; padding: 20px; background: #f0f2f5; min-height: 100vh;`;
const HeaderCard = styled.div`background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1); margin-bottom: 25px;`;
const CoverImage = styled.img`width: 100%; height: 280px; object-fit: cover; background: #eee;`;
const ProfileBranding = styled.div`display: flex; padding: 0 30px 25px; margin-top: -60px; gap: 20px; align-items: flex-end;`;
const LogoWrapper = styled.div`width: 150px; height: 160px; background: white; border-radius: 15px; border: 5px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.1); overflow: hidden;`;
const Logo = styled.img`width: 100%; height: 100%; object-fit: contain;`;
const BusinessInfoMain = styled.div`h1 { margin: 0; font-size: 24px; text-transform: capitalize; } .tagline { color: #666; font-size: 18px; margin: 5px 0; }`;
const StatsContainer = styled.div`display: flex; gap: 10px; margin-top: 5px;`;
const StatBadge = styled.span`background: #005b63; color: white; padding: 4px 12px; border-radius: 20px; font-size: 13px; font-weight: bold;`;
const ContentGrid = styled.div`display: grid; grid-template-columns: 320px 1fr; gap: 20px; @media (max-width: 768px) { grid-template-columns: 1fr; }`;
const Sidebar = styled.div``;
const InfoCard = styled.div`background: white; padding: 25px; border-radius: 12px; h3 { margin-top: 0; margin-bottom: 15px; }`;
const Divider = styled.div`height: 1px; background: #eee; margin: 15px 0;`;
const ContactList = styled.ul`list-style: none; padding: 0; li { margin-bottom: 10px; font-size: 14px; }`;
const MainFeed = styled.div``;
const PostCard = styled.div`background: white; border-radius: 12px; padding: 20px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.05);`;
const PostDateTop = styled.div`color: #888; font-size: 12px; margin-bottom: 10px; border-bottom: 1px solid #f9f9f9;`;
const PostText = styled.div`font-size: 16px; margin-bottom: 15px; line-height: 1.5; white-space: pre-wrap;`;
const PostMedia = styled.img`width: 100%; border-radius: 8px;`;
const VideoWrapper = styled.div`position: relative; padding-bottom: 56.25%; height: 0; border-radius: 10px; overflow: hidden; iframe { position: absolute; width: 100%; height: 100%; top: 0; left: 0; }`;
const LoadingContainer = styled.div`text-align: center; padding: 100px; font-weight: bold; color: #1877f2;`;
const FollowersSection = styled.div`background: white; padding: 20px; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);`;
const FollowersGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 15px; margin-top: 15px;`;
const FollowerCard = styled.div`border: 1px solid #eef0f2; border-radius: 10px; padding: 15px; text-align: center; display: flex; flex-direction: column; align-items: center; transition: transform 0.2s; &:hover { transform: translateY(-3px); }`;
const FollowerImageContainer = styled.div`width: 70px; height: 70px; border-radius: 50%; overflow: hidden; background: #f0f2f5; margin-bottom: 10px; border: 2px solid #fff; box-shadow: 0 2px 5px rgba(0,0,0,0.1); img { width: 100%; height: 100%; object-fit: cover; }`;
const FollowerName = styled.h4`margin: 5px 0 2px; font-size: 14px; color: #333; font-weight: 700;`;
const BusinessLink = styled.div`color: #1877f2; font-size: 12px; font-weight: 600; cursor: pointer; margin-top: 4px; &:hover { text-decoration: underline; }`;
const FollowerType = styled.p`font-size: 11px; color: #777; margin-bottom: 10px;`;
const ViewProfileButton = styled.button`width: 100%; background: #e7f3ff; color: #1877f2; border: none; padding: 8px 0; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; &:hover { background: #dbeaff; }`;

export default BusinessView;